import React from 'react';

export interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Alignment of the timeline */
  align?: 'left' | 'right' | 'alternate';
  /** Position of the line */
  position?: 'left' | 'right' | 'center';
  /** Color of the timeline connector */
  connectorColor?: 'gray' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  /** Style of the timeline connector */
  connectorStyle?: 'solid' | 'dashed' | 'dotted';
  /** Whether the timeline is reversed */
  reverse?: boolean;
}

export interface TimelineItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Position of the item */
  position?: 'left' | 'right';
  /** Whether the item should appear to connect to the next item */
  connector?: boolean;
  /** Whether the item is the last one */
  last?: boolean;
}

export interface TimelineDotProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Color of the dot */
  color?: 'gray' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  /** Size of the dot */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the dot is outlined */
  outlined?: boolean;
  /** Custom icon to display in the dot */
  icon?: React.ReactNode;
}

export interface TimelineContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional alignment override */
  align?: 'left' | 'right';
}

export interface TimelineSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

export interface TimelineConnectorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  /** Color of the connector */
  color?: 'gray' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  /** Style of the connector */
  styleType?: 'solid' | 'dashed' | 'dotted';
}

const TimelineContext = React.createContext<{
  align: 'left' | 'right' | 'alternate';
  position: 'left' | 'right' | 'center';
  connectorColor: 'gray' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  connectorStyle: 'solid' | 'dashed' | 'dotted';
  reverse: boolean;
}>({
  align: 'left',
  position: 'left',
  connectorColor: 'gray',
  connectorStyle: 'solid',
  reverse: false,
});

export const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  (
    {
      align = 'left',
      position = 'left',
      connectorColor = 'gray',
      connectorStyle = 'solid',
      reverse = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Modify children to add position prop based on the alignment and reverse prop
    const childrenWithProps = React.Children.map(
      children,
      (child, index) => {
        if (!React.isValidElement(child)) return child;

        const isLast = index === React.Children.count(children) - 1;
        let itemPosition: 'left' | 'right' = position === 'center' 
          ? (align === 'alternate' ? (index % 2 === 0 ? 'left' : 'right') : align) 
          : position;
          
        // If reversed, flip alternate positions
        if (reverse && align === 'alternate') {
          itemPosition = itemPosition === 'left' ? 'right' : 'left';
        }
        
        return React.cloneElement(child as React.ReactElement<any>, {
          position: itemPosition,
          last: isLast,
        });
      }
    );
    
    // If reverse is true, reverse the order of the children
    const orderedChildren = reverse 
      ? React.Children.toArray(childrenWithProps).reverse() 
      : childrenWithProps;
    
    return (
      <TimelineContext.Provider
        value={{ align, position, connectorColor, connectorStyle, reverse }}
      >
        <div
          ref={ref}
          className={`relative ${className}`}
          {...props}
        >
          {orderedChildren}
        </div>
      </TimelineContext.Provider>
    );
  }
);

Timeline.displayName = 'Timeline';

export const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
  (
    {
      position: positionProp,
      connector = true,
      last = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const { position: contextPosition } = React.useContext(TimelineContext);
    
    // Use position from props or from context
    const position = positionProp || contextPosition;
    
    // Item styles based on position
    const positionClasses = position === 'center'
      ? 'grid grid-cols-[1fr_auto_1fr] items-start'
      : 'flex items-start';
    
    // Modify children to pass props like connector and position
    const childrenWithProps = React.Children.map(
      children,
      (child) => {
        if (!React.isValidElement(child)) return child;
        
        const childType = (child.type as any)?.displayName;
        
        if (childType === 'TimelineConnector') {
          return React.cloneElement(child as React.ReactElement<any>, {
            hidden: last,
          });
        }
        
        if (childType === 'TimelineContent') {
          return React.cloneElement(child as React.ReactElement<any>, {
            align: position,
          });
        }
        
        return child;
      }
    );
    
    return (
      <div
        ref={ref}
        className={`
          ${positionClasses}
          ${position === 'right' ? 'flex-row-reverse' : ''}
          mb-4 last:mb-0
          ${className}
        `}
        {...props}
      >
        {childrenWithProps}
      </div>
    );
  }
);

TimelineItem.displayName = 'TimelineItem';

export const TimelineDot = React.forwardRef<HTMLDivElement, TimelineDotProps>(
  (
    {
      color = 'primary',
      size = 'md',
      outlined = false,
      icon,
      className = '',
      ...props
    },
    ref
  ) => {
    // Size classes
    const sizeClasses = {
      sm: 'w-2 h-2',
      md: 'w-3 h-3',
      lg: 'w-4 h-4',
    };
    
    // Icon size classes
    const iconSizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
    };
    
    // Color classes
    const colorClasses = {
      gray: outlined 
        ? 'border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800' 
        : 'bg-gray-400 dark:bg-gray-500',
      primary: outlined 
        ? 'border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800' 
        : 'bg-blue-500 dark:bg-blue-400',
      secondary: outlined 
        ? 'border-2 border-purple-500 dark:border-purple-400 bg-white dark:bg-gray-800' 
        : 'bg-purple-500 dark:bg-purple-400',
      success: outlined 
        ? 'border-2 border-green-500 dark:border-green-400 bg-white dark:bg-gray-800' 
        : 'bg-green-500 dark:bg-green-400',
      warning: outlined 
        ? 'border-2 border-yellow-500 dark:border-yellow-400 bg-white dark:bg-gray-800' 
        : 'bg-yellow-500 dark:bg-yellow-400',
      danger: outlined 
        ? 'border-2 border-red-500 dark:border-red-400 bg-white dark:bg-gray-800' 
        : 'bg-red-500 dark:bg-red-400',
    };
    
    // Icon color classes (for outlined dots)
    const iconColorClasses = {
      gray: 'text-gray-400 dark:text-gray-400',
      primary: 'text-blue-500 dark:text-blue-400',
      secondary: 'text-purple-500 dark:text-purple-400',
      success: 'text-green-500 dark:text-green-400',
      warning: 'text-yellow-500 dark:text-yellow-400',
      danger: 'text-red-500 dark:text-red-400',
    };
    
    if (icon) {
      return (
        <div
          ref={ref}
          className={`
            flex items-center justify-center
            rounded-full
            z-10
            ${outlined ? 'p-1' : 'p-2'}
            ${colorClasses[color]}
            ${className}
          `}
          {...props}
        >
          <div className={`${iconSizeClasses[size]} ${outlined ? iconColorClasses[color] : 'text-white'}`}>
            {icon}
          </div>
        </div>
      );
    }
    
    return (
      <div
        ref={ref}
        className={`
          rounded-full
          z-10
          ${sizeClasses[size]}
          ${colorClasses[color]}
          ${className}
        `}
        {...props}
      />
    );
  }
);

TimelineDot.displayName = 'TimelineDot';

export const TimelineContent = React.forwardRef<HTMLDivElement, TimelineContentProps>(
  (
    {
      align,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const positionClasses = align === 'right' ? 'ml-4' : 'mr-4';
    
    return (
      <div
        ref={ref}
        className={`
          flex-1
          pt-0.5
          ${positionClasses}
          ${className}
        `}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TimelineContent.displayName = 'TimelineContent';

export const TimelineSeparator = React.forwardRef<HTMLDivElement, TimelineSeparatorProps>(
  (
    {
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={`
          flex flex-col items-center
          ${className}
        `}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TimelineSeparator.displayName = 'TimelineSeparator';

export const TimelineConnector = React.forwardRef<HTMLDivElement, TimelineConnectorProps>(
  (
    {
      color,
      styleType,
      hidden = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const { connectorColor, connectorStyle } = React.useContext(TimelineContext);
    
    // Use color and style from props or from context
    const finalColor = color || connectorColor;
    const finalStyle = styleType || connectorStyle;
    
    // Color classes
    const colorClasses = {
      gray: 'bg-gray-300 dark:bg-gray-600',
      primary: 'bg-blue-400 dark:bg-blue-500',
      secondary: 'bg-purple-400 dark:bg-purple-500',
      success: 'bg-green-400 dark:bg-green-500',
      warning: 'bg-yellow-400 dark:bg-yellow-500',
      danger: 'bg-red-400 dark:bg-red-500',
    };
    
    // Style classes
    const styleClasses = {
      solid: '',
      dashed: 'border-dashed border-l-2 bg-transparent',
      dotted: 'border-dotted border-l-2 bg-transparent',
    };
    
    // Border color classes for dashed and dotted styles
    const borderColorClasses = {
      gray: 'border-gray-300 dark:border-gray-600',
      primary: 'border-blue-400 dark:border-blue-500',
      secondary: 'border-purple-400 dark:border-purple-500',
      success: 'border-green-400 dark:border-green-500',
      warning: 'border-yellow-400 dark:border-yellow-500',
      danger: 'border-red-400 dark:border-red-500',
    };
    
    return (
      <div
        ref={ref}
        className={`
          ${hidden ? 'hidden' : 'flex-1 min-h-[20px] w-0.5 my-1'}
          ${finalStyle === 'solid' ? colorClasses[finalColor] : borderColorClasses[finalColor]}
          ${styleClasses[finalStyle]}
          ${className}
        `}
        {...props}
      />
    );
  }
);

TimelineConnector.displayName = 'TimelineConnector';

// Export as a namespace object for convenient usage
const TimelineNamespace = Object.assign(Timeline, {
  Item: TimelineItem,
  Dot: TimelineDot,
  Content: TimelineContent,
  Separator: TimelineSeparator,
  Connector: TimelineConnector,
});

export default TimelineNamespace;